import { Form } from '@ant-design/compatible';
import { Select } from 'antd';
import Spinner from 'common/spinner/components/spinner';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import moment from 'utils/cops-moment';
import { withRouter } from 'utils/withRouter';
// eslint-disable-next-line
import ReferenceDataDropdown from 'common/reference-data/containers/reference-data-dropdown-container';
import {
  Button,
  DatePicker,
  DropdownButton,
  Flex,
  FormItem,
  Input,
  InputNumber,
  TimePicker,
} from 'ui';
import { absenceUnits } from '../../../../../utils/app-configs';
import { blockConfig, viewConfig } from '../../../config';

const StyledBlock = styled.div`
  padding-top: 16px;
  margin-bottom: 10px;
`;

const StyledSaveButton = styled(Button)`
  text-transform: uppercase;
`;

const StyledFormItem = styled(FormItem)`
  .ant-legacy-form-item-required::before {
    display: none;
  }
`;

const StyledDatePickerFormItem = styled(StyledFormItem)`
  width: min-content;
`;

const StyledDatePicker = styled(DatePicker)`
  padding: 0 10px 0 0 !important;
  width: 150px !important;

  input {
    height: 34px !important;
    padding-left: 10px !important;
    font-size: 16px !important;
  }
`;

const StyledTimePicker = styled(TimePicker)`
  width: 125px !important;
`;

const CancelButton = styled(Button)`
  margin-right: 12px;
  width: 140px;
  text-transform: uppercase;
`;

const StyledInput = styled(Input)`
  width: 60%;
`;

const StyledDurationFormItem = styled(StyledFormItem)`
  width: min-content;

  .ant-legacy-form-explain {
    position: fixed;
  }
`;

const StyledUnitFormItem = styled(StyledFormItem)`
  margin-top: auto !important;

  .ant-select-selector {
    width: auto !important;
    color: #313b40;
    font-weight: 400;
    border: 1px solid #b9b9b9 !important;
    font-size: 16px !important;
    border-radius: 3px;
    height: 34px !important;
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const StyledDropdownButton = styled(DropdownButton)`
.ant-dropdown-button {
  .ant-btn {
    text-transform: uppercase;
  }
  .ant-btn:first-child {
    width: 160px;
  }
}
`;

// eslint-disable-next-line complexity
const AbsenceEditSidepanelContent = ({
  match,
  intl,
  block,
  form,
  isLoadingBlockData,
  isDoingAction,
  updateBlock,
  createBlock,
  isNew = false,
  view = {},
  lanesInfo,
  onEngineerChange,
  readOnly,
  close,
  publishToSAP,
  deletePlanning,
}) => {
  const { getFieldDecorator } = form;

  const calculateDurationValue = (blockToCalc) => {
    if (blockToCalc?.duration) {
      if (blockToCalc.durationDisplayUnit === absenceUnits.hours) {
        return moment.duration(blockToCalc?.duration).asHours();
      } else if (blockToCalc.durationDisplayUnit === absenceUnits.days) {
        // MW will cut off minutes from days for better display
        // if the unit is days -> calculate how many days is it and round it up to get the whole
        const duration = moment.duration(blockToCalc?.duration).asDays();
        return Math.ceil(duration);
      }
    }
    return moment.duration('01:00:00').asHours();
  };

  const transformValues = values => values?.filter(v => !v.isRegion);

  const handleSave = () => {
    form.validateFields((err) => {
      if (!err) {
        if (isNew) {
          createBlock();
        } else {
          updateBlock();
        }
      }
    });
  };

  getFieldDecorator('laneId', {
    initialValue: isNew ? match.params.laneId : block?.laneId,
  });
  const engineerNumberValidator = getFieldDecorator('technicianNumber', {
    initialValue: isNew ?
      match.params.laneId :
      block?.laneId,
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.FIELD_ENGINEER' }) }],
  });
  const startDateValidator = getFieldDecorator('startDate', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.START_DATE' }) }],
    initialValue: isNew ? moment(match.params.date, 'YYYYMMDD') : moment(block?.start),
  });
  const startWorkingDay = view?.parameters?.startWorkingDay || '08:00:00';
  const startTimeValidator = getFieldDecorator('startTime', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.START_TIME' }) }],
    initialValue: isNew ? moment(startWorkingDay, 'HH:mm:ss') : moment(block?.start),
  });

  const descriptionAbsenceValidator = getFieldDecorator('descriptionAbsence', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.DESCRIPTION_ABSENCE' }) }],
    initialValue: block?.line1,
  });

  const durationAbsenceValidator = getFieldDecorator('durationAbsence', {
    rules: [
      { required: true, message: intl.formatMessage({ id: 'rules.DURATION_ERROR' }) },
      {
        validator: (_, value) =>
          // null value should pass because 2 error messages will show
          value === null || value > 0 ? Promise.resolve() : new Error(intl.formatMessage({
            id: 'planning.errors.durationCannotBeZero',
          })),
      },
    ],
    initialValue:
      // eslint-disable-next-line
      isNew ? moment.duration('01:00:00').asHours() : calculateDurationValue(block),
  });

  const durationDisplayUnitValidator = getFieldDecorator('durationDisplayUnit', {
    initialValue: isNew ? absenceUnits.days : block?.durationDisplayUnit ?? absenceUnits.hours,
  });

  const disabled = isLoadingBlockData || isDoingAction || readOnly;

  const text = match.params.type === blockConfig.types.absence ?
    intl.formatMessage({ id: 'planningBoard.blocks.SAVE_TIME_OFF' }) :
    intl.formatMessage({ id: 'planningBoard.blocks.SAVE_RESERVATION' });

  const handleDurationDisplayUnitChange = (value) => {
    form.setFieldsValue({
      durationDisplayUnit: value,
    });
  };

  return isLoadingBlockData ?
    <Spinner /> :
    <>
      <StyledBlock>
        <Form layout='vertical'>
          <StyledFormItem
            label={
              intl.formatMessage({ id: 'order.fieldEngineer.FIELD_SERVICE_ENGINEER' })
            }
            data-test-id='absence-sp-fse-absence'
          >
            {engineerNumberValidator(
              <ReferenceDataDropdown
                width={'60%'}
                isSorted={true}
                disabled={disabled || isNew}
                // eslint-disable-next-line max-len
                endpoint={`planning/${viewConfig.boardIdTypes.service}/lanes/${isNew ? lanesInfo?.find(l => l.id === match.params.laneId)?.plant : block.plant}`}
                placeholder={
                  intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })
                }
                type='order'
                name='workCenters'
                valueKey='id'
                valueLabel='name'
                onObjectChanged={onEngineerChange}
                transformValues={transformValues}
                serviceCenterId={isNew ?
                  lanesInfo?.find(l => l.id === match.params.laneId)?.plant :
                  block?.plant
                }
                isPlanningBoard={true}
                useBodyAsParent={true}
              />,
            )}
          </StyledFormItem>
          <StyledFormItem
            label={
              intl.formatMessage({ id: 'planningBoard.blocks.DESCRIPTION' })
            }
          >
            {descriptionAbsenceValidator(
              <StyledInput
                placeholder={intl.formatMessage({
                  id: 'planningBoard.blocks.DESCRIPTION_PLACEHOLDER',
                })}
                disabled={disabled}
                data-test-id='absence-sp-absence-description'
              />,
            )}
          </StyledFormItem>
          <Flex ffrn width='60%' colGap={10}>
            <StyledDatePickerFormItem
              label={intl.formatMessage({ id: 'planningBoard.blocks.START_DATE' })}
            >
              {startDateValidator(
                <StyledDatePicker
                  disabled={disabled}
                  useBodyAsParent={true}
                  data-test-id='abscence-start-date'
                />,
              )}
            </StyledDatePickerFormItem>
            {form.getFieldValue('durationDisplayUnit') === absenceUnits.hours &&
              <StyledFormItem
                label={
                  intl.formatMessage({ id: 'planningBoard.blocks.START_TIME' })
                }
                colon={false}
              >
                {startTimeValidator(
                  <StyledTimePicker
                    allowClear={false}
                    showSecond={false}
                    format='HH:mm'
                    disabled={disabled}
                    minuteStep={15}
                    useBodyAsParent={true}
                  />,
                )}
              </StyledFormItem>
            }
          </Flex>
          <Flex ffrn width='60%' colGap={10} marginBottom={30}>
            <StyledDurationFormItem
              label={intl.formatMessage({ id: 'planningBoard.blocks.DURATION' })}
              colon={false}
            >
              {durationAbsenceValidator(
                <InputNumber
                  min={0}
                  disabled={disabled}
                  precision={
                    // eslint-disable-next-line no-undefined
                    form.getFieldValue('durationDisplayUnit') === absenceUnits.days ? 0 : undefined
                  }
                  data-test-id='duration-absence'
                />,
              )}
            </StyledDurationFormItem>
            <StyledUnitFormItem
              colon={false}
            >
              {durationDisplayUnitValidator(
                <Select
                  options={Object.values(absenceUnits)?.map(unit => ({
                    value: unit,
                    label: intl.formatMessage({
                      id: `planningBoard.blocks.absenceUnits.${unit.toUpperCase()}`,
                    }),
                  }))}
                  onChange={handleDurationDisplayUnitChange}
                  disabled={disabled}
                  data-test-id='duration-display-unit'
                  getPopupContainer={p => document.getElementsByClassName('fullscreen')[0] ?? p}
                />,
              )}
            </StyledUnitFormItem>
          </Flex>
          <Flex jcfs marginTop={20}>
            <CancelButton type='secondary' light onClick={close} data-test-id='absence-sp-cancel'>
              {intl.formatMessage({ id: 'common.CANCEL' })}
            </CancelButton>
            {block?.actions?.length === 1 ||
              isNew ?
              // eslint-disable-next-line
              <StyledSaveButton
                onClick={handleSave}
                disabled={disabled}
                type='secondary'
                data-test-id='btn-absence-sp-save'
              >
                {text}
              </StyledSaveButton> :
              <StyledDropdownButton
                text={match.params.type === blockConfig.types.absence ?
                  intl.formatMessage({ id: 'planningBoard.blocks.SAVE_TIME_OFF' }) :
                  intl.formatMessage({ id: 'planningBoard.blocks.SAVE_RESERVATION' })
                }
                uppercase={true}
                onClick={handleSave}
                dataTestId='dropdown-absence-sp-save'
                disabled={isLoadingBlockData || isDoingAction || readOnly}
                subItems={[
                  {
                    id: blockConfig.blockActions.savePlanning,
                    text,
                    onClick: updateBlock,
                  },
                  {
                    id: blockConfig.blockActions.publishToSAP,
                    text: intl.formatMessage({ id: 'planning.actions.publishToSap.description' }),
                    onClick: () => publishToSAP({
                      boardId: viewConfig.boardIdTypes.service,
                      planningIds: [block.id],
                    }),
                  },
                  {
                    id: blockConfig.blockActions.deletePlanning,
                    text: intl.formatMessage({ id: 'planning.actions.delete.description' }),
                    onClick: deletePlanning,
                  },
                ]?.filter(a =>
                  block?.actions?.find(ba => ba.id === a.id) ||
                  a.id === blockConfig.blockActions.savePlanning,
                )?.sort((a, b) => {
                  const first = block?.actions?.find(ba => ba.id === a.id);
                  const second = block?.actions?.find(ba => ba.id === b.id);
                  if (a.id === blockConfig.blockActions.savePlanning) {
                    return -1;
                  }
                  return first?.priority - second?.priority;
                })}
              >
                <FormattedMessage id='planningBoard.blocks.SAVE_PLANNING' />
              </StyledDropdownButton>
            }
          </Flex>
        </Form>
      </StyledBlock>
    </>;
};

AbsenceEditSidepanelContent.propTypes = {
  match: PropTypes.object,
  intl: PropTypes.object,
  block: PropTypes.object,
  form: PropTypes.object,
  isLoadingBlockData: PropTypes.bool,
  isDoingAction: PropTypes.bool,
  updateBlock: PropTypes.func,
  isNew: PropTypes.bool,
  createBlock: PropTypes.func,
  view: PropTypes.object,
  lanesInfo: PropTypes.array,
  onEngineerChange: PropTypes.func,
  readOnly: PropTypes.bool,
  close: PropTypes.func,
  publishToSAP: PropTypes.func,
  deletePlanning: PropTypes.func,
};

export default withRouter(injectIntl(AbsenceEditSidepanelContent));
